<template>
  <aside class="filter-sidebar">
    <div class="filter-sidebar__section" v-if="!domainConfig?.themeFilters?.type || domainConfig.themeFilters.type === 'all'">
      <div class="filter-sidebar__title">Type</div>
      <div :class="['filter-sidebar__options', { 'is-expanded': expanded.type }]" ref="typeOptions">
        <div v-for="(option, idx) in filterOptions.types" :key="idx"
          :class="['filter-sidebar__option', { 'is-active': selectedFilters.type === option.value }]"
          @click="$emit('update-filter', 'type', option.value, $event)">
          {{ option.label }}
        </div>
      </div>
      <div v-if="overflow.type" class="filter-sidebar__more" @click="toggleExpand('type')">
        {{ expanded.type ? 'Show less' : 'Show more' }}
      </div>
    </div>

    <div class="filter-sidebar__section">
      <div class="filter-sidebar__title">Ex-Showroom Price</div>
      <div :class="['filter-sidebar__options', { 'is-expanded': expanded.price }]" ref="priceOptions">
        <div v-for="(option, idx) in filterOptions.prices" :key="idx"
          :class="['filter-sidebar__option',{ 'is-active': selectedFilters.price === option.value }]"
          @click="$emit('update-filter', 'price', option.value, $event)">
          {{ option.label }}
        </div>
      </div>
      <div v-if="overflow.price" class="filter-sidebar__more" @click="toggleExpand('price')">
        {{ expanded.price ? 'Show less' : 'Show more' }}
      </div>
    </div>

    <div class="filter-sidebar__section" v-if="!domainConfig?.themeFilters?.fuel || domainConfig.themeFilters.fuel === 'all'">
      <div class="filter-sidebar__title">Fuel</div>
      <div :class="['filter-sidebar__options', { 'is-expanded': expanded.fuel }]" ref="fuelOptions">
        <div v-for="(option, idx) in filterOptions.fuels" :key="idx"
          :class="['filter-sidebar__option',{ 'is-active': selectedFilters.fuel === option.value }]"
          @click="$emit('update-filter', 'fuel', option.value, $event)">
          {{ option.label }}
        </div>
      </div>
      <div v-if="overflow.fuel" class="filter-sidebar__more" @click="toggleExpand('fuel')">
        {{ expanded.fuel ? 'Show less' : 'Show more' }}
      </div>
    </div>

    <div class="filter-sidebar__section" v-if="!domainConfig?.themeFilters?.purpose || domainConfig.themeFilters.purpose === 'all'">
      <div class="filter-sidebar__title">Purpose</div>
      <div :class="['filter-sidebar__options', { 'is-expanded': expanded.purpose }]" ref="purposeOptions">
        <div v-for="(option, idx) in filterOptions.purposes" :key="idx"
          :class="['filter-sidebar__option',{ 'is-active': selectedFilters.purpose === option.value }]"
          @click="$emit('update-filter', 'purpose', option.value, $event)">
          {{ option.label }}
        </div>
      </div>
      <div v-if="overflow.purpose" class="filter-sidebar__more" @click="toggleExpand('purpose')">
        {{ expanded.purpose ? 'Show less' : 'Show more' }}
      </div>
    </div>

    <div class="filter-sidebar__section" v-if="!domainConfig?.themeFilters?.brand || domainConfig.themeFilters.brand === 'all'">
      <div class="filter-sidebar__title">Brands</div>
      <div :class="['filter-sidebar__options', { 'is-expanded': brandExpanded }]" ref="brandOptions">
        <div v-for="(brand, idx) in visibleBrands" :key="idx"
          :class="['filter-sidebar__option',{ 'is-active': selectedFilters.brand === brand.value }]"
          @click="$emit('update-filter', 'brand', brand.value, $event)">
          {{ brand.label }}
        </div>
      </div>
      <div v-if="overflow.brand" class="filter-sidebar__more" @click="toggleExpand('brand')">
        {{ brandExpanded ? 'Show less' : 'Show more' }}
      </div>
    </div>
  </aside>
</template>

<script>
export default {
  name: 'FilterSidebar',
  props: {
    filterOptions: { type: Object, required: true },
    selectedFilters: { type: Object, required: true },
    visibleBrands: { type: Array, default: () => [] },
    brandExpanded: { type: Boolean, default: false },
  },
  data() {
    return {
      expanded: {
        type: false,
        price: false,
        fuel: false,
        purpose: false
      },
      overflow: {
        type: false,
        price: false,
        fuel: false,
        purpose: false,
        brand: false
      }
    }
  },
  watch: {
    filterOptions: {
      handler() {
        this.checkAllOverflow();
      },
      deep: true
    },
    visibleBrands() {
      this.checkAllOverflow();
    }
  },
  mounted() {
    this.checkAllOverflow();
    window.addEventListener('resize', this.checkAllOverflow);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.checkAllOverflow);
  },
  methods: {
    checkAllOverflow() {
      this.$nextTick(() => {
        ['type', 'price', 'fuel', 'purpose', 'brand'].forEach(key => {
          const el = this.$refs[key + 'Options'];
          if (el) {
            const isCurrentlyExpanded = key === 'brand' ? this.brandExpanded : this.expanded[key];
            
            if (isCurrentlyExpanded) {
              // If it's already expanded, we check scrollHeight against a reasonable threshold for 4 lines
              // 180px is a good estimate for 4 lines of options
              this.$set(this.overflow, key, el.scrollHeight > 180);
            } else {
              this.$set(this.overflow, key, el.scrollHeight > el.clientHeight + 5);
            }
          }
        });
      });
    },
    toggleExpand(key) {
      if (key === 'brand') {
        this.$emit('toggle-brand');
      } else {
        this.expanded[key] = !this.expanded[key];
      }
      this.$nextTick(() => {
        this.checkAllOverflow();
      });
    }
  }
}
</script>

<style lang="scss" >
@import '~@/css/variables.scss';

.filter-sidebar {
	color: #fff;
}

.filter-sidebar__section {
	margin-bottom: rem(24);
}

.filter-sidebar__title {
	color: #e2372d;
	font-size: rem(12);
	text-transform: uppercase;
	margin-bottom: rem(12);
	font-weight: 600;
	letter-spacing: 0.5px;
}

.filter-sidebar__options {
	display: flex;
	flex-wrap: wrap;
	gap: rem(10);
	max-height: rem(180);
	overflow: hidden;

	&.is-expanded {
		max-height: none;
	}
}

.filter-sidebar__option {
	padding: rem(8) rem(14);
	border-radius: rem(18);
  background: rgba(226, 55, 45, 0.1);
  color: rgba(255, 255, 255, 0.60);
	cursor: pointer;
	font-size: rem(13);
	transition: all 0.2s ease;
	border: rem(1) solid rgba(255, 255, 255, 0.05);

	&:hover {
		background: rgba(255, 255, 255, 0.12);
	}

	&.is-active {
		background: #e2372d;
		color: #fff;
		border-color: #e2372d;
	}
}

.filter-sidebar__more {
	margin-top: rem(8);
	color: #e2372d;
	cursor: pointer;
	font-size: rem(13);
	transition: opacity 0.2s ease;

	&:hover {
		opacity: 0.8;
	}
}
</style>
